import { useEffect } from 'react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from './useViewport';

/**
 * Desktop-only keyboard shortcuts (Phase 6.4).
 *   D     → toggle main panel (Dispatch24 OS)
 *   M     → toggle mute
 *   Space → toggle pause (no-op in realistic mode)
 *   Esc   → close active panel / build mode
 *
 * On tablet/mobile this hook is a no-op to avoid hijacking on-screen-keyboard
 * input on form fields.
 */
export function useKeyboardShortcuts(): void {
  const viewport = useViewport();

  const mainPanelOpen = useGameStore((s) => s.mainPanelOpen);
  const openMainPanel = useGameStore((s) => s.openMainPanel);
  const closeMainPanel = useGameStore((s) => s.closeMainPanel);
  const selectedDetail = useGameStore((s) => s.selectedDetail);
  const clearDetail = useGameStore((s) => s.clearDetail);
  const buildMode = useGameStore((s) => s.buildMode);
  const setBuildMode = useGameStore((s) => s.setBuildMode);
  const toggleMute = useGameStore((s) => s.toggleMute);
  const togglePause = useGameStore((s) => s.togglePause);

  useEffect(() => {
    if (viewport !== 'desktop') return;

    const onKey = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (mainPanelOpen) closeMainPanel();
        else openMainPanel();
      } else if (e.key.toLowerCase() === 'm') {
        e.preventDefault();
        toggleMute();
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePause();
      } else if (e.key === 'Escape') {
        // ESC priority chain — same as Game.tsx but redundant safe net
        if (buildMode) {
          setBuildMode(null);
        } else if (selectedDetail) {
          clearDetail();
        } else if (mainPanelOpen) {
          closeMainPanel();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    viewport,
    mainPanelOpen,
    openMainPanel,
    closeMainPanel,
    selectedDetail,
    clearDetail,
    buildMode,
    setBuildMode,
    toggleMute,
    togglePause,
  ]);
}
